import type { FC } from 'react'
import { useEffect } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import adminQueries from '@/modules/admin/queries'
import type {
  HrOffboardingReport,
  OnboardingReport,
  ReportFilters,
} from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import { useReportsContext } from '../index.context'
import { getOnOffboardingExportRows } from '../onOffboardingReportHelpers'

type OnOffboardingReport = OnboardingReport | HrOffboardingReport

type Props = {
  filters: ReportFilters
  type: 'onboarding' | 'offboarding'
}

export const OnOffboarding: FC<Props> = ({ filters, type }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<
    keyof OnboardingReport | keyof HrOffboardingReport
  >()
  const isOnboarding = type === 'onboarding'
  const queryKey = isOnboarding
    ? adminQueries.adminOnboardingReports(filters)
    : adminQueries.adminHrOffboardingReports(filters)

  const { data: reports = [] } = useQuery<OnOffboardingReport[]>(
    queryKey,
    async () => {
      const reports = isOnboarding
        ? await adminResources.getOnboardingReports(filters)
        : await adminResources.getHrOffboardingReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const stringMatcher = (item: OnOffboardingReport) =>
    Object.values(getOnOffboardingExportRows([item], type)[0])

  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)
  const total = reportsFiltered.length

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="employeeName">
                <Translation id="Employee Name" />
              </Th>
              <Th onClick={handleSort} id="company">
                <Translation id="Company" />
              </Th>
              <Th onClick={handleSort} id="department">
                <Translation id="Department" />
              </Th>
              <Th onClick={handleSort} id={isOnboarding ? 'startDate' : 'endDate'}>
                <Translation id={isOnboarding ? 'Start Date' : 'End Date'} />
              </Th>
              <Th onClick={handleSort} id="createdDate">
                <Translation id="Created Date" />
              </Th>
              <Th onClick={handleSort} id="status">
                <Translation id="Status" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map((report, index) => (
              <tr key={`${report.employeeNumber || report.employeeName}-${index}`}>
                <Td>
                  <Highlight text={report.employeeName ?? undefined} pattern={searchFilter} />
                </Td>
                <Td>
                  <Highlight text={report.company ?? undefined} pattern={searchFilter} />
                </Td>
                <Td>
                  <Highlight text={report.department ?? undefined} pattern={searchFilter} />
                </Td>
                <Td>
                  <Highlight
                    text={
                      isOnboarding
                        ? ((report as OnboardingReport).startDate ?? undefined)
                        : ((report as HrOffboardingReport).endDate ?? undefined)
                    }
                    pattern={searchFilter}
                  />
                </Td>
                <Td>
                  <Highlight text={report.createdDate ?? undefined} pattern={searchFilter} />
                </Td>
                <Td>
                  <Highlight text={report.status ?? undefined} pattern={searchFilter} />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
